import { LearnMoreLink, Toggle } from '@ui/themed';
import { t } from 'i18next';
import { featureEnabled } from '../../../constants/features';
import { useEnv } from '../../../context/env-context';
import { useHandlers, useSite } from '../../../context/page-context';
import type { Setting } from '../../../context/settings-context';
import { useSettings, useUpdateSettings } from '../../../context/settings-context';
import { cn } from '../../../lib/styles';
import { PARTNERS_I18N } from '../constants';
import type { Partner } from '../types';
import { PartnerBetaWaitlist } from './partner-beta-waitlist';
import { PartnerSyncStatus } from './partner-sync-status';
import { PartnerUpsell } from './partner-upsell';

const PAGE = PARTNERS_I18N.page;

export function PartnerRow({
  partner,
  onConfigure,
}: {
  partner: Partner;
  onConfigure: (partner: Partner) => void;
}) {
  const env = useEnv();
  const site = useSite();
  const settings = useSettings();
  const { mutate } = useUpdateSettings();
  const { notificationHandler } = useHandlers();

  const isEnabled = Boolean(settings[partner.settingKey]);
  const isAdvancedFeaturesEnabled = !!env.billing.is_advanced_features_enabled;
  const needsUpgrade = partner.isAdvancedOnly && !isAdvancedFeaturesEnabled;
  // A beta partner is only switchable once it has a feature flag *and* that flag is on for this
  // site; everyone else is offered the waitlist instead.
  const isWaitlisted =
    !!partner.beta &&
    (!partner.feature || !featureEnabled(partner.feature, site.siteId, env.platform));
  const canEnable = !needsUpgrade && !isWaitlisted;

  function handleToggle(next: boolean) {
    if (next) {
      onConfigure(partner);
      return;
    }
    // On failure the optimistic update flips the toggle back on, which on its own reads as a click
    // that didn't register — say so explicitly, as the partner modals do when their save fails.
    mutate({ key: partner.settingKey, value: false } as Setting, {
      onError: () => notificationHandler.error(),
    });
  }

  return (
    <li>
      <div className={cn('flex items-center p-4', partner.beta && 'bg-gray-100')}>
        <div className="flex min-w-0 flex-1 items-center">
          <span className="size-9 min-w-9">
            <img
              src={partner.logo}
              className="size-9 rounded-full border-2 border-gray-300"
              alt={t(`${PAGE}.logo_alt`, { partner: partner.name })}
            />
          </span>
          <div className="ml-3">
            <p className="truncate font-medium text-gray-900 text-sm">
              {partner.name}
              {partner.beta && <span className="text-gray-500"> {t(`${PAGE}.coming_soon`)}</span>}
            </p>
            <p className="flex flex-wrap items-center gap-x-1 text-gray-500 text-sm">
              {t(partner.description)}
              <LearnMoreLink link={partner.docsUrl} />
            </p>
          </div>
        </div>

        {isEnabled && (
          <div className="mr-4">
            <button
              type="button"
              className="inline-flex cursor-pointer items-center font-medium text-indigo-600 text-sm hover:text-indigo-500"
              onClick={() => onConfigure(partner)}
            >
              {t(`${PAGE}.modify`)}
            </button>
          </div>
        )}

        {canEnable && <Toggle checked={isEnabled} onChange={handleToggle} />}
        {!canEnable && needsUpgrade && <PartnerUpsell partnerName={partner.name} />}
        {!canEnable && !needsUpgrade && <PartnerBetaWaitlist />}
      </div>

      {isEnabled && partner.sync && <PartnerSyncStatus dataTypes={partner.sync} />}
    </li>
  );
}
